/**
 * @fileoverview Footer module for the Curtis Institute of Music WordPress theme.
 * This module handles the footer logo animation using Lottie animations.
 * It randomly selects between different animation variations and loads them
 * dynamically to create visual interest in the site footer.
 * 
 * @module footer
 */

import lottie from 'lottie-web';

/**
 * Footer module interface
 * @interface FooterModule
 */
interface FooterModule {
  elements: {
    logo: HTMLElement | null;
  };
  init: () => void;
  bindEvents: () => Promise<void>;
  getPath: () => string;
}

/**
 * Footer module implementation that manages footer animations
 * @type {FooterModule}
 */
const footer: FooterModule = {
  /**
   * DOM elements used by the footer module
   * @property {HTMLElement|null} logo - The footer logo container element
   */
  elements: {
    logo: document.querySelector('.site-footer__logo'),
  },

  /**
   * Initializes the footer module
   * Sets up event bindings and starts the animation loading process
   * 
   * @returns {void}
   * @example
   * footer.init(); // Initializes footer animations
   */
  init() {
    this.bindEvents();
  },

  /**
   * Binds events and loads the Lottie animation
   * Fetches the animation JSON file and initializes the Lottie player
   * 
   * @async
   * @returns {Promise<void>}
   * @example
   * await footer.bindEvents(); // Loads and starts the footer animation
   */
  async bindEvents() {
    if(!this.elements.logo) return;

    try {
      const response = await fetch(this.getPath());
      
      // Check if response is successful
      if (!response.ok) {
        console.warn(`Failed to fetch footer animation: ${response.status} ${response.statusText}`);
        return;
      }

      // Check if response has content and is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn('Footer animation response is not JSON:', contentType);
        return;
      }

      // Check if response body exists
      const text = await response.text();
      if (!text.trim()) {
        console.warn('Footer animation response is empty');
        return;
      }

      // Parse JSON with error handling
      let file;
      try {
        file = JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse footer animation JSON:', parseError);
        return;
      }

      // Validate animation data structure
      if (!file || typeof file !== 'object') {
        console.warn('Invalid footer animation data structure');
        return;
      }

      lottie.loadAnimation({
        container: this.elements.logo,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: file,
      });

    } catch (error) {
      console.error('Error loading footer animation:', error);
    }
  },

  /**
   * Generates a random animation file path
   * Randomly selects between available animation variations (1 or 7)
   * to provide visual variety on page loads
   * 
   * @returns {string} The full URL path to the selected animation JSON file
   * @example
   * const animationPath = footer.getPath();
   * // Returns: "https://curtis.test/wp-content/themes/curtis/public/animations/Curtis_Footer_Animation_1.json"
   * // or: "https://curtis.test/wp-content/themes/curtis/public/animations/Curtis_Footer_Animation_7.json"
   */
  getPath() {
    const numbers = [ 1, 7 ];
    const number = Math.floor(Math.random() * numbers.length);
    return `${window.curtis.themeurl}/public/animations/Curtis_Footer_Animation_${numbers[number]}.json`;
  }
};

export default footer;
